import { del, get, set } from "idb-keyval";
import merge from "lodash.merge";
import { v4 as uuidv4 } from "uuid";
import { create, StoreApi } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { Claims } from "@/features/auth/components/ClaimsContext";
import { Flag } from "@/features/auth/const/flags";
import { DATE_WHEN_OFFLINE_KEY } from "@/features/connectivity/components/ConnectivityProvider";
import { trackPendingWrite } from "@/features/storage/pendingWrites";

import { CompanySlice, createCompanySlice } from "./slices/companySlice";
import { createOrganizationSlice, OrganizationSlice } from "./slices/organizationSlice";
import { createPersonSlice, PersonSlice } from "./slices/personSlice";
import { createPharmacyProductSlice, PharmacyProductSlice } from "./slices/pharmacyProductSlice";
import { createProductHealthInsuranceSlice, ProductHealthInsuranceSlice } from "./slices/productHealthInsurance";
import { createSaleSlice, SaleSlice } from "./slices/salesSlice";

export type Setter<Store> = (
  partial: ((state: Store) => void) | Partial<Store> | Store,
  replace?: boolean | undefined,
) => void;
export type Getter<Store> = () => Store;

export type Slice<T> = (set: Setter<BoundStore>, get: Getter<BoundStore>, store: StoreApi<BoundStore>) => T;

export type BoundStore = {
  _hasHydrated: boolean;
  sessionId: string;
  claims: Claims | undefined;
  company: CompanySlice;
  fetchAllData: () => Promise<void>;
  organization: OrganizationSlice;
  person: PersonSlice;
  pharmacyId: string | undefined;
  pharmacyProduct: PharmacyProductSlice;
  productHealthInsurance: ProductHealthInsuranceSlice;
  resetData: (claims: Claims) => void;
  sale: SaleSlice;
  setClaims: (claims: Claims | undefined) => Promise<void>;
  setHasHydrated: (hasHydrated: boolean) => void;
};
/**
 * Using multiple slice is a best practice in the doc (https://github.com/pmndrs/zustand?tab=readme-ov-file#best-practices)
 * Doc about slices: https://github.com/pmndrs/zustand/blob/main/docs/guides/slices-pattern.md
 */
export const useBoundStore = create<BoundStore>()(
  persist(
    immer((set, get, store) => ({
      _hasHydrated: false,
      claims: undefined,
      company: createCompanySlice(set, get, store),
      fetchAllData: async () => {
        // prevent to fetch data offline
        if (localStorage.getItem(DATE_WHEN_OFFLINE_KEY)) {
          return;
        }
        const state = get();
        const isAdmin = ((state.claims?.flags ?? 0) & Flag.IS_ADMIN) !== 0;

        if (state.claims?.pin && !isAdmin && state._hasHydrated) {
          await state?.pharmacyProduct.fetchPharmacyProducts();
          await state?.productHealthInsurance.fetchProductHealthInsurances();
          await state?.person.fetchPersons();
          await state?.company.fetchCompanies();
          await state?.organization.fetchOrganizations();
        }
      },
      sessionId: uuidv4(),
      organization: createOrganizationSlice(set, get, store),
      person: createPersonSlice(set, get, store),
      pharmacyId: undefined,
      productHealthInsurance: createProductHealthInsuranceSlice(set, get, store),
      pharmacyProduct: createPharmacyProductSlice(set, get, store),
      resetData: (claims: Claims) => {
        set((state) => {
          state.pharmacyId = claims.pharmacyId;
          state.claims = claims;

          state.company.resetData();
          state.organization.resetData();
          state.person.resetData();
          state.pharmacyProduct.resetData();
          state.sale.resetSales();
        });
      },
      sale: createSaleSlice(set, get, store),
      setClaims: async (claims) => {
        set((state) => {
          state.claims = claims;
        });
        if (claims) {
          get().fetchAllData();
        }
      },
      setHasHydrated: (hasHydrated: boolean) => {
        set((state) => {
          state._hasHydrated = hasHydrated;
        });
      },
    })),
    {
      merge: (persistedState, currentState) => merge(currentState, persistedState),
      name: "zustand-storage",
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
      partialize: (state) => ({
        claims: state.claims,
        company: state.company,
        organization: state.organization,
        person: state.person,
        pharmacyId: state.pharmacyId,
        pharmacyProduct: state.pharmacyProduct,
        productHealthInsurance: state.productHealthInsurance,
        sale: state.sale,
      }),
      storage: createJSONStorage(() => ({
        getItem: async (name: string): Promise<null | string> => {
          return (await get(name)) || null;
        },
        // zustand/persist appelle setItem sans jamais attendre la promesse : un quota sature
        // remontait donc en unhandledrejection. trackPendingWrite a deja signale la cause.
        removeItem: async (name: string): Promise<void> => {
          await trackPendingWrite(del(name)).catch(() => undefined);
        },
        setItem: async (name: string, value: string): Promise<void> => {
          await trackPendingWrite(set(name, value)).catch(() => undefined);
        },
      })),
    },
  ),
);
